"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useRevealOnScroll } from '@/hooks/useRevealOnScroll';
function FadeIn({
  children,
  delay = 0,
  className = ""
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div ref={ref} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${className}`} style={{
    transitionDelay: `${delay}ms`
  }}>
      {children}
    </div>;
}
export default function AboutEditorial() {
  const content = {
    title: "Pourquoi j’accompagne les mères après la naissance",
    subtitle: "Mon propre post-partum m’a montré à quel point le soutien des mères est essentiel.",
    intro: ["Hello ! Je m’appelle Isabel et je vis à Nyon. Je suis maman d’un petit garçon, dont la naissance est venue transformer profondément ma vie.", "Depuis plus de dix ans, j’accompagne les enfants et leurs familles, avec une attention particulière portée au développement des bébés. Je me suis formée en tant qu’éducatrice Montessori pour les enfants de 0 à 3 ans, et j’ai exercé pendant des années au sein d’écoles Montessori.", "C’est à la suite de mon propre post-partum que quelque chose s’est éclairé pour moi. J’ai alors compris combien j’avais à cœur d’accompagner les parents durant cette phase si sensible, afin de leur offrir un post-partum plus doux, plus soutenu et plus serein."],
    experience: ["Depuis, j’ai enrichi mon parcours grâce à des formations spécialisées en accompagnement postnatal à domicile, en soins du nouveau-né et en allaitement, notamment auprès d’une consultante IBCLC. J’ai également approfondi mes connaissances autour du sommeil du tout-petit, afin de pouvoir accompagner les familles de manière globale et ajustée à leur réalité.", "Mon approche est globale, douce et profondément respectueuse. Je m’intéresse particulièrement aux soins corporels et aux approches naturelles qui soutiennent la récupération, l’apaisement et le lien. C’est dans cette continuité que je me forme actuellement en réflexologie plantaire, avec une spécialisation en périnatalité, convaincue de la puissance du toucher pour soutenir les grandes transitions de la vie."],
    formationsTitle: "Formations et certifications",
    formationsIntro: "Mon accompagnement s’appuie sur plus de dix ans d’expérience auprès des enfants et des familles, ainsi que sur les formations suivantes :",
    formationsList: ["Certificat en réflexologie plantaire – Réflexe Santé, Morges (en cours)", "Certification Accompagnante Postnatale Le Mois d’Or (en cours)", "Certification « Newborn Care Specialist » – Antenatal Training International", "Formation sur le sommeil du nourrisson et du jeune enfant (0–5 ans) – Petit Monde", "Formation « L’essentiel de l’allaitement » avec Carole Hervé, consultante IBCLC", "Certification en psychologie de l’enfance – NCC Education, Royaume-Uni", "Diplôme de Guide Montessori pour les enfants de 0 à 3 ans (AMI)"],
    moisDorIntro: "Avec Le Mois d’Or, j’ai été formée par les meilleurs spécialistes en périnatalité :",
    moisDorList: ["Céline Chadelat et Marie Mahé-Poulin, co-autrices et fondatrices de la formation Le Mois d’Or", "Jaques Dayan, professeur en pédopsychiatrie", "Ingrid Bayot, Sage-femme et formatrice en périnatalité et allaitement", "Dr Bernadette de Gasquet, Médecin et professeur de yoga", "Delphine Petit-Postma, Kraamzorg", "Candice Levy, Naturopathe"],
    missionTitle: "Ma mission",
    missionText: ["Dans une période de profonde transformation, je remets au centre ce qui compte vraiment : la présence et le respect du rythme propre à chaque famille. Pour les familles habituées à un rythme effréné, j’aide à faciliter la transition vers la parentalité en créant un espace pour ralentir, se reposer et récupérer plus profondément.", "Pour celles qui aspirent à un cocon doux et nourrissant, je deviens une présence stable et bienveillante tout au long du parcours post-partum.", "Mon soutien est concret, personnalisé et ancré dans vos propres choix et valeurs. En contribuant à créer les conditions d’un post-partum plus doux et plus soutenu, je permets à la mère de récupérer physiquement et émotionnellement, pour que les liens puissent se développer dans la confiance, la connexion et la sérénité."],
    imaginezTitle: "Imaginez…",
    imaginezText: ["Imaginez un matin sans logistique, où votre seule mission est de vous imprégner de ces précieux moments avec votre nouveau-né, sans la pression de « gérer la maison ». Je m’occupe du reste.", "J’aide à créer l’espace nécessaire pour ralentir vraiment, afin que votre récupération ne devienne pas un défi de plus à relever, mais une transition plus douce et plus soutenue.", "Je suis là pour atténuer le bruit du monde extérieur et vous permettre de vous recharger — physiquement et émotionnellement — afin que vous puissiez retrouver progressivement votre énergie avec un sentiment renouvelé de calme et d’équilibre."],
    finalTitle: "Peu importe votre point de départ…",
    finalText: "Ma mission reste la même : vous offrir un soutien concret et bienveillant. Que vous ayez besoin d’une main tendue pour adopter un rythme plus lent, ou d’une présence douce pour protéger et nourrir votre cocon, je m’adapte à la réalité unique de votre famille.",
    quote: "« La période du post-partum est un moment clé dans la santé d’une femme. La manière dont une mère est soutenue aujourd’hui peut influencer son bien-être pour les années à venir. »",
    cta: "Voir les options d’accompagnement",
    ctaLink: "/AccommodationsPage"
  };
  return <section id="about" className="bg-background text-foreground overflow-hidden py-24 md:py-32">
      <div className="container mx-auto max-w-5xl px-6 md:px-12">
        {}
        <FadeIn className="mx-auto max-w-3xl text-center mb-20 md:mb-32">
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight mb-8 text-foreground">
            {content.title}
          </h2>
          
        </FadeIn>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center mb-24 md:mb-32">
          <FadeIn className="lg:col-span-7 space-y-6">
            {content.intro.map((paragraph, i) => <p key={i} data-index={i} className="font-sans text-base md:text-lg leading-relaxed text-foreground/90">
                {paragraph}
              </p>)}
          </FadeIn>
          <FadeIn delay={200} className="lg:col-span-5">
            <AspectRatio ratio={3 / 4} className="overflow-hidden rounded-lg">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/isabel_portrait_vertical.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/isabel_portrait_vertical.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/isabel_portrait_vertical.webp-wvc-sizes" alt="Portrait d'Isabel" className="object-cover w-full h-full" loading="lazy" src="https://quality-chicken.10web.cloud/wp-content/uploads/2026/06/unnamed-scaled.jpg" />
            </AspectRatio>
          </FadeIn>
        </div>

        {}
        <FadeIn className="mx-auto max-w-3xl space-y-6 mb-24 md:mb-32">
          {content.experience.map((paragraph, i) => <p key={i} data-index={i} className="font-sans text-base md:text-lg leading-relaxed text-foreground/90">
              {paragraph}
            </p>)}
        </FadeIn>

        {}
        <FadeIn className="mx-auto max-w-3xl mb-24 md:mb-32">
          <h2 className="font-serif text-3xl md:text-4xl mb-8 text-foreground">
            {content.formationsTitle}
          </h2>
          <p className="font-sans text-base md:text-lg leading-relaxed text-foreground/90 mb-6">
            {content.formationsIntro}
          </p>
          <ul className="space-y-3 mb-12">
            {content.formationsList.map((item, i) => <li key={i} data-index={i} className="font-sans text-base md:text-lg text-foreground/90 flex items-start">
                <span className="mr-3 text-primary mt-1">•</span>
                <span>{item}</span>
              </li>)}
          </ul>

          <p className="leading-relaxed text-foreground/90 mb-6 !text-3xl !font-serif"><span className="!text-3xl"><span className="!font-semibold">Avec Le Mois d’Or, j’ai été formée par les meilleurs spécialistes en périnatalité:</span></span></p>
          <ul className="space-y-3">
            {content.moisDorList.map((item, i) => <li key={i} data-index={i} className="font-sans text-base md:text-lg text-foreground/90 flex items-start">
                <span className="mr-3 text-primary mt-1">•</span>
                <span>{item}</span>
              </li>)}
          </ul>
        </FadeIn>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center mb-24 md:mb-32">
          <FadeIn className="lg:col-span-6 space-y-6 order-2 lg:order-1">
            <h2 className="font-serif text-3xl md:text-4xl mb-8 text-foreground">
              {content.missionTitle}
            </h2>
            {content.missionText.map((paragraph, i) => <p key={i} data-index={i} className="font-sans text-base md:text-lg leading-relaxed text-foreground/90">
                {paragraph}
              </p>)}
          </FadeIn>
          <FadeIn delay={200} className="lg:col-span-6 order-1 lg:order-2">
            <AspectRatio ratio={4 / 5} className="overflow-hidden rounded-lg">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/soft_postpartum_care.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/soft_postpartum_care.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/soft_postpartum_care.png-wvc-sizes" alt="Soins post-partum doux" className="object-cover w-full h-full" loading="lazy" src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/5b2fc64b319d4110274fd743f220ac9d.jpg" />
            </AspectRatio>
          </FadeIn>
        </div>

        {}
        <FadeIn className="mx-auto max-w-3xl text-center mb-24 md:mb-32">
          <h2 className="font-serif text-3xl md:text-4xl mb-8 text-foreground">
            {content.imaginezTitle}
          </h2>
          <div className="space-y-6 text-left md:text-center">
            {content.imaginezText.map((paragraph, i) => <p key={i} data-index={i} className="font-sans text-base md:text-lg leading-relaxed text-foreground/90">
                {paragraph}
              </p>)}
          </div>
        </FadeIn>

        {}
        <FadeIn className="mx-auto max-w-3xl mb-20">
          <h3 className="font-serif text-2xl md:text-3xl mb-6 text-foreground">
            {content.finalTitle}
          </h3>
          <p className="font-sans text-base md:text-lg leading-relaxed text-foreground/90 mb-16">
            {content.finalText}
          </p>

          <blockquote className="border-l-2 border-destructive pl-6 md:pl-8 py-2 my-16">
            <p className="font-serif italic text-xl md:text-2xl leading-relaxed text-foreground/80">
              {content.quote}
            </p>
          </blockquote>
        </FadeIn>

        {}
        <FadeIn className="text-center pb-12">
          <Button asChild variant="outline" size="lg" className="rounded-lg border-primary text-primary hover:bg-primary hover:text-primary-foreground px-8 py-6 text-base md:text-lg font-sans tracking-wide transition-colors">
            <Link to="/AccommodationsPage">{content.cta}</Link>
          </Button>
        </FadeIn>
      </div>
    </section>;
}